import { ChangeDetectorRef, Component, inject, ViewChild } from '@angular/core';
import { DynamicFormService } from '@ntap/common';
import {
  AttributeStateService,
  Item,
  ListItemProviderDirective,
  ViewProviderDirective
} from '@ntap/core';
import { PrimeNGInputDropdownComponent } from '@ntap/primeng';
import { Dropdown } from 'primeng/dropdown';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  first,
  mergeMap,
  skip,
  Subject,
  switchMap,
  tap
} from 'rxjs';

@Component({
  selector: 'app-lazy-dropdown-input-control',
  template: `
    <ng-container *ngIf="inputConfig$ | async as inputConfig" class="w-full">
      <p-dropdown
        #dropdown
        [name]="inputConfig.formControlName"
        [formControl]="$any(formControl)"
        [disabled]="inputConfig.disabled"
        [options]="options"
        (onFilter)="filter($event)"
        (onLazyLoad)="lazyLoad($event)"
        [virtualScroll]="virtualScroll"
        [virtualScrollItemSize]="itemSize"
        [required]="inputConfig.required"
        [readonly]="inputConfig.readOnly"
        [filter]="true"
        [lazy]="true"
        [showClear]="!inputConfig.readOnly && !inputConfig.required"
        [class]="'w-full block input-' + inputConfig.formControlName"
        [ngClass]="{ readonly: inputConfig.readOnly }"
        emptyFilterMessage="Nema rezultata"
        [placeholder]="'<Odaberite>'"
        (onChange)="onOptionsChange()"
        appendTo="body"
      >
      </p-dropdown>
      <ntap-show-form-errors [control]="formControl"></ntap-show-form-errors>
    </ng-container>
  `,
  styleUrls: ['./lazy-dropdown-input-control.component.scss']
})
export class LazyDropdownInputControlComponent extends PrimeNGInputDropdownComponent {
  private readonly chunkSize = 50;
  private itemProvider = inject(ListItemProviderDirective, { optional: true });
  private viewProvider = inject(ViewProviderDirective, { optional: true });
  private filterEvent = new BehaviorSubject<any>({ filter: null });
  private lazyLoadEvent = new Subject<Range & { $query: string }>();

  @ViewChild('dropdown')
  private dropdown: Dropdown;

  override itemSize = 35;

  constructor(
    formService: DynamicFormService,
    attributeStateService: AttributeStateService,
    private cdr: ChangeDetectorRef
  ) {
    super(formService, attributeStateService);
    this.initLazyLoadSubscription();
    if (this.itemProvider) {
      this.addSubscription(
        this.itemProvider.item$
          .pipe(
            switchMap((item) => {
              return this.formControl$.pipe(
                switchMap((fc) => {
                  return fc.valueChanges.pipe(
                    tap((v) => {
                      // TODO provjeriti ako ovo treba - ne bi trebalo biti hardkodirano
                      if (this.formControlName === 'sv~mjesto~mjesto_id') {
                        this.transferSelectedLabelToLabelAttribute(
                          'en~mjesto~glavni_naziv',
                          item,
                          v
                        );
                      } else if (
                        this.formControlName === 'sv~autor~identifikator'
                      ) {
                        this.transferSelectedLabelToLabelAttribute(
                          'en~agent~naziv',
                          item,
                          v
                        );
                      }
                    })
                  );
                })
              );
            })
          )
          .subscribe()
      );
    }
  }

  private transferSelectedLabelToLabelAttribute(
    labelAttrName: string,
    item: Item,
    v: any
  ) {
    const updatedItem = Object.assign({}, item, {
      [labelAttrName]: this.options
        .filter((o: { label: string; value: any }) => o.value === v)
        .map((o: { label: string; value: any }) => o.label)
        .join(',')
    });
    this.itemProvider?.setItem(updatedItem);
  }

  public filter(event: any) {
    this.options = [];
    this.filterEvent.next({
      $query: event.filter,
      $first: event.first,
      $last: event.last
    });
  }

  public lazyLoad(event: any) {
    this.lazyLoadEvent.next({
      $first: event.first,
      $last: event.last,
      $query: event.filter
    });
  }

  private initLazyLoadSubscription() {
    return this.addSubscription(
      combineLatest([
        this.filterEvent.pipe(debounceTime(200)),
        this.lazyLoadEvent.pipe(
          distinctUntilChanged((_, current: Range) => {
            // ignore sve sto je manje od trenutno max offseta u visekratnicima chunkSize
            // console.log('Currently loaded: ' + this.options.length)
            const currentMaxLoaded =
              this.calculateDiscreteNextChunkOffset(this.options.length) -
              this.chunkSize;
            if (
              (current.$first >= currentMaxLoaded - 17 ||
                current.$last >= currentMaxLoaded) &&
              this.options.length >= this.chunkSize // 17 je empirijski, to je otprilike koliko stane u viewport kod ovog itemSize
            ) {
              console.log(
                'Currently max loaded: ' +
                  currentMaxLoaded +
                  ', first: ' +
                  current.$first +
                  ',last: ' +
                  current.$last
              );
              console.log('Continue lazy load.');
              // console.log(current);
              return false;
            }

            // console.log('Ignore lazy load, already loaded:');
            // console.log(current);
            return true;
          })
        )
      ])
        .pipe(
          debounceTime(150),
          skip(1),
          switchMap(([filterEvent, lazyEvent]) => {
            return this.inputConfig$.pipe(
              first(),
              mergeMap((conf) => {
                const params = {
                  $query: filterEvent.$query,
                  $first: this.options.length,
                  $last: this.calculateDiscreteNextChunkOffset(lazyEvent.$last)
                };
                return this.attributeStateService
                  .getAllowedValues(
                    conf.inputControlData.attributeMetadata,
                    Object.assign(params, this.getCurrentObjectState()),
                    this.viewProvider?.viewName || ''
                  )
                  .pipe(
                    tap((result) => {
                      this.options = [...this.options, ...result];
                      this.cdr.detectChanges();
                      setTimeout(() => {
                        const scrollerElement =
                          document.querySelector('p-scroller')!;
                        const childDiv = scrollerElement.querySelector('div')!;
                        childDiv.style.height =
                          this.options.length != 0
                            ? this.options.length * this.itemSize + 'px'
                            : this.itemSize + 'px';
                      }, 0);
                    })
                  );
              })
            );
          })
        )
        .subscribe()
    );
  }

  public onOptionsChange() {
    const dropdownPanel: any = document.querySelector('.p-dropdown-panel');
    if (dropdownPanel) {
      dropdownPanel.style.height = 'auto'; // or set to a specific height
    }
  }

  private calculateDiscreteNextChunkOffset(length: number) {
    return (
      Math.trunc(length / this.chunkSize) * this.chunkSize + this.chunkSize
    );
  }

  override getCurrentObjectState() {
    const currentObjectState: any = super.getCurrentObjectState();

    if (!currentObjectState['$existingId']) {
      currentObjectState['$existingId'] = this.formService.getFormValue(
        this.inputParam.attributeMetadata.name
      );
    }

    return currentObjectState;
  }
}

class Range {
  constructor(public $first: number, public $last: number) {}
}
